import { Link } from "@tanstack/react-router";
import { CaileveOrbitBoard } from "@/components/caileve-orbit-board";
import { track } from "@/lib/analytics";
import { type MoodBoardItem } from "@/components/item-detail-sections";
import { cleanEditorialLine } from "@/lib/narrative-utils";
import { WeatherIndicator } from "@/components/weather-indicator";
import type { WeatherBand, WeatherCondition } from "@/lib/weather";
import { formatMissingSlotsMessage } from "@/lib/style-engine/daily-look-selection";
import { bgFields } from "@/lib/photo-preference";

export function LookOfDaySection({
  request,
  selectedItemRefs,
  jewelryRows,
  clothingRows,
  isLoading,
}: {
  request: {
    id: string;
    event: string | null;
    season: string | null;
    time_of_day: string | null;
    suggested_look: string | null;
    weather_context?: unknown;
    missing_slots?: string[] | null;
  } | null;
  selectedItemRefs: Array<{ id: string; type: string }>;
  jewelryRows: Array<{ id: string; name: string; photo_url: string | null; photo_url_no_bg?: string | null; prefer_clean_bg?: boolean | null; clean_bg_color?: string | null }>;
  clothingRows: Array<{ id: string; name: string; photo_url: string | null; photo_url_no_bg?: string | null; prefer_clean_bg?: boolean | null; clean_bg_color?: string | null }>;
  isLoading?: boolean;
}) {
  const editorialLine = request?.suggested_look
    ? cleanEditorialLine(request.suggested_look)
    : null;
  const missingSlotsMessage = formatMissingSlotsMessage(request?.missing_slots ?? null);

  // Rebuilt from selected_item_ids in insertion order.
  // type "accessory" entries resolve against jewelryRows,
  // since bags and shoes live in jewelry_items.
  const moodItems: MoodBoardItem[] = selectedItemRefs
    .map((ref) => {
      if (ref.type === "clothing") {
        const row = clothingRows.find((r) => r.id === ref.id);
        return row ? { type: "clothing" as const, id: row.id, name: row.name, photo_url: row.photo_url, ...bgFields(row) } : null;
      }
      const row = jewelryRows.find((r) => r.id === ref.id);
      if (!row) return null;
      return ref.type === "accessory"
        ? { type: "accessory" as const, id: row.id, name: row.name, photo_url: row.photo_url, ...bgFields(row) }
        : { type: "jewelry" as const, id: row.id, name: row.name, photo_url: row.photo_url, ...bgFields(row) };
    })
    .filter((i): i is MoodBoardItem => i !== null);

  const contextParts = [request?.event, request?.season, request?.time_of_day]
    .filter((p): p is string => !!p && p.trim().length > 0);
  const contextLine = contextParts.join(" · ");

  const wc = request?.weather_context as
    | { band?: WeatherBand; condition?: WeatherCondition; source?: string }
    | null
    | undefined;
  const hasWeather = !!wc && wc.source !== "unavailable" && (!!wc.band || !!wc.condition);

  const TRANSITION = "opacity 600ms ease";

  return (
    <section className="max-w-5xl mx-auto px-8 pt-2 pb-6">
      <div className="flex items-end justify-between mb-4">
        <div>
          <p
            className="text-[10px] uppercase tracking-[0.2em]"
            style={{ color: "var(--caileve-lilac)" }}
          >
            ✦ Today's Cailève Look
          </p>
          <div style={{ position: "relative", height: 18, marginTop: 6 }}>
            <div
              aria-hidden="true"
              className="animate-pulse rounded"
              style={{
                position: "absolute",
                inset: 0,
                height: 10,
                width: 120,
                backgroundColor: "var(--caileve-soft)",
                opacity: isLoading ? 1 : 0,
                transition: TRANSITION,
                pointerEvents: "none",
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                opacity: isLoading ? 0 : 1,
                transition: TRANSITION,
              }}
            >
              {hasWeather && (
                <WeatherIndicator
                  condition={wc?.condition ?? null}
                  band={wc?.band ?? null}
                />
              )}
              <p
                className="text-[11px] uppercase tracking-[0.25em]"
                style={{ color: "var(--caileve-muted)", margin: 0 }}
              >
                {contextLine || "\u00A0"}
              </p>
            </div>
          </div>
        </div>
        <Link
          to="/requests/$id"
          params={{ id: request?.id ?? "" }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            whiteSpace: "nowrap",
            paddingTop: 10,
            paddingBottom: 10,
            paddingLeft: 20,
            paddingRight: 20,
            backgroundColor: "var(--caileve-gold)",
            color: "var(--caileve-ink)",
            fontSize: 13,
            letterSpacing: "0.08em",
            fontFamily: "Inter, sans-serif",
            borderRadius: "var(--caileve-radius-pill)",
            opacity: isLoading || !request ? 0 : 1,
            pointerEvents: isLoading || !request ? "none" : "auto",
            transition: TRANSITION,
          }}
        >
          View Look
        </Link>
      </div>

      <div className="flex flex-col items-center">
        <div style={{ width: "100%", maxWidth: 340, position: "relative" }}>
          {/* Aspect-ratio holder keeps container height stable — 1:1 for orbit board */}
          <div style={{ width: "100%", aspectRatio: "1 / 1" }} />
          <div
            aria-hidden="true"
            className="animate-pulse w-full rounded"
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "var(--caileve-soft)",
              opacity: isLoading ? 1 : 0,
              transition: TRANSITION,
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              opacity: isLoading ? 0 : 1,
              transition: TRANSITION,
              pointerEvents: isLoading ? "none" : "auto",
            }}
          >
            {moodItems.length > 0 && (
              <CaileveOrbitBoard
                items={moodItems}
                altName="Your look of the day"
                compact
              />
            )}
          </div>
        </div>

        <div
          className="mt-5 w-full relative"
          style={{ maxWidth: 480, minHeight: 48 }}
        >
          <div
            aria-hidden="true"
            style={{
              opacity: isLoading ? 1 : 0,
              transition: TRANSITION,
              pointerEvents: "none",
              position: "absolute",
              inset: 0,
            }}
          >
            <div
              className="animate-pulse rounded mx-auto"
              style={{ height: 12, width: "85%", backgroundColor: "var(--caileve-soft)" }}
            />
            <div
              className="animate-pulse rounded mx-auto mt-2"
              style={{ height: 12, width: "65%", backgroundColor: "var(--caileve-soft)" }}
            />
          </div>
          <p
            className="italic text-base leading-relaxed text-center"
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              color: "var(--caileve-ink)",
              opacity: isLoading ? 0 : 1,
              transition: TRANSITION,
            }}
          >
            {editorialLine ?? "\u00A0"}
          </p>
        </div>

        {missingSlotsMessage && !isLoading && (
          <p
            className="mt-3 text-center text-[12px]"
            style={{
              color: "var(--caileve-muted)",
              fontFamily: "Inter, sans-serif",
              letterSpacing: "0.02em",
            }}
          >
            {missingSlotsMessage}
          </p>
        )}
      </div>

      <div
        aria-hidden="true"
        className="mt-8"
        style={{ borderBottom: "1px solid var(--caileve-soft)" }}
      />
    </section>
  );
}
